/**
 * The pieces a vendor runtime needs to be started BEFORE the patient's tap and handed to the scan that
 * runs after it (docs/decisions.md D-63).
 *
 * The start screen is the only time a patient spends in this app before the scan in which the vendor
 * is already known - the scan config names it - and no camera is open. Both vendors' heaviest step
 * needs neither a camera nor a canvas: Shen.AI's `load()` is a 33 MB download and a WASM compile,
 * Mindset's `init()` a worker, its WASM and four MediaPipe models. Started there, that time is spent
 * while the patient reads, rather than at the concurrent hybrid's barrier, where on an iPhone 12 Shen.AI
 * waited 19 s for Mindset (D-62).
 *
 * Nothing here knows a vendor. The browser clients own one slot each and decide what goes in it; this
 * file is what has decisions in it and can therefore be tested without a camera or a WASM module.
 */

/**
 * One runtime started early, taken at most once.
 *
 * Taken ONCE because a runtime is a scan's: the scan that takes it tears it down when it ends, and a
 * second scan in the same page (a retry) must load its own rather than be handed one already destroyed.
 */
export interface WarmSlot<T> {
  /** Starts the runtime unless one is already held. A no-op once one is held, so a re-render cannot start two. */
  start(begin: () => T): void
  /** The held runtime, or null when none was started or it was already taken. Empties the slot. */
  take(): T | null
  readonly held: boolean
}

export function createWarmSlot<T>(): WarmSlot<T> {
  let value: { readonly current: T } | null = null

  return {
    start(begin) {
      if (value) return
      value = { current: begin() }
    },
    take() {
      const taken = value
      value = null
      return taken ? taken.current : null
    },
    get held() {
      return value !== null
    },
  }
}

/**
 * A download's progress, reported before anybody is listening for it.
 *
 * The warm runtime starts reporting the moment its download starts, on the start screen, where there is
 * no bar to move. The scan that takes it subscribes later and must see where the download has got to,
 * not wait for the next report - which, for a download that has already finished, never comes.
 */
export interface ProgressRelay {
  report(fractionComplete: number): void
  /** The latest fraction at once, when there is one, and every later one. Replaces any earlier listener. */
  listen(listener: (fractionComplete: number) => void): void
}

export function createProgressRelay(): ProgressRelay {
  let latest: number | null = null
  let listener: ((fractionComplete: number) => void) | null = null

  return {
    report(fractionComplete) {
      latest = fractionComplete
      listener?.(fractionComplete)
    },
    listen(next) {
      listener = next
      if (latest !== null) next(latest)
    },
  }
}

/**
 * Events a vendor emitted before the scan subscribed to them.
 *
 * Mindset announces the end of `init()` with a `ready` EVENT, and its session runner waits for that
 * event rather than for the promise (mindset-session.ts). Warmed on the start screen, `ready` has been
 * emitted - to nobody - long before the scan subscribes, and a session waiting for it would wait out its
 * whole ready timeout and fail. So the warm runtime records what it emits until the scan adopts it, and
 * the scan's subscriptions are given the record.
 */
export interface EventReplay {
  /** The listener to subscribe on the vendor for `event`. Records until `seal()`. */
  recorder(event: string): (payload: unknown) => void
  /**
   * Stops recording. From here the vendor's own subscriptions deliver, and a payload emitted after this
   * is not in the record - so nothing is delivered twice.
   */
  seal(): void
  /** Every payload recorded for `event`, in the order emitted, to `listener` - each on a microtask, as a vendor event would arrive. */
  replay(event: string, listener: (payload: unknown) => void): void
}

/**
 * One line for the diagnostics panel when a scan adopts a warm runtime: how long before the scan it was
 * started, and whether it had finished. The panel's clock restarts with the scan, so without this a warm
 * start and a fast cold load would read the same.
 */
export function describeAdoption(startedAt: number, settled: boolean, now: number = Date.now()): string {
  const seconds = ((now - startedAt) / 1000).toFixed(1)
  return `adopted, started ${seconds}s earlier, ${settled ? 'already loaded' : 'still loading'}`
}

export function createEventReplay(): EventReplay {
  const recorded: { readonly event: string; readonly payload: unknown }[] = []
  let sealed = false

  return {
    recorder(event) {
      return (payload) => {
        if (!sealed) recorded.push({ event, payload })
      }
    },
    seal() {
      sealed = true
    },
    replay(event, listener) {
      for (const entry of recorded) {
        if (entry.event === event) {
          queueMicrotask(() => listener(entry.payload))
        }
      }
    },
  }
}
